import { useQuery } from '@tanstack/react-query'
import { get, qs } from './api'
import { normalizeRole } from './auth'
import type { CourierAccount, CourierCatalog, PaymentGateway, Shop, TeamMember, Thresholds } from './types'

/** Reference data used across many pages. Long staleTime — it rarely changes mid-session. */

export const useShops = () =>
  useQuery({ queryKey: ['shops'], queryFn: () => get<Shop[]>('/shops'), staleTime: 60_000 })

export const useTeam = () =>
  useQuery({
    queryKey: ['team'],
    queryFn: async () => (await get<TeamMember[]>('/team')).map((m) => ({ ...m, role: normalizeRole(m.role) })),
    staleTime: 5 * 60_000,
  })

/**
 * What can be connected and what each form asks for. Static per deploy, so
 * cached for the session. Forms are generated from this — no courier is
 * hardcoded in the SPA.
 */
export const useCourierCatalog = () =>
  useQuery({ queryKey: ['courier-catalog'], queryFn: () => get<CourierCatalog>('/courier-accounts/catalog'), staleTime: Infinity })

export const useCourierAccounts = () =>
  useQuery({ queryKey: ['courier-accounts'], queryFn: () => get<CourierAccount[]>('/courier-accounts') })

export const useGateways = (shopId: string | undefined) =>
  useQuery({
    queryKey: ['gateways', shopId],
    queryFn: () => get<PaymentGateway[]>(`/shops/${shopId}/payment-gateways`),
    enabled: !!shopId,
  })

/** Thresholds are per shop in the schema; the contract has no shop param yet (CONTRACT-GAPS). */
export const useThresholds = (shopId: string | undefined) =>
  useQuery({
    queryKey: ['thresholds', shopId],
    queryFn: () => get<Thresholds>(`/settings/thresholds${qs({ shop_id: shopId })}`),
    enabled: !!shopId,
    staleTime: 5 * 60_000,
  })

/** userId -> display name, for assignee columns. */
export function useMemberNames(): (id: string | null | undefined) => string | null {
  const team = useTeam()
  return (id) => {
    if (!id) return null
    const m = team.data?.find((t) => t.userId === id)
    return m ? (m.name ?? m.email) : 'Someone'
  }
}
